import { useContext, useMemo, useState } from "react";
import { Alert as AlertComponent, Button } from '@aidkitorg/component-library';
import InterfaceContext from "../Context";
import { safeParse, GoBackToSectionButton } from "../Util";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { QuestionProps } from "./Props";
import type { BulkDownload, VisitLink } from "@aidkitorg/types/lib/survey";
import { useActionExecutor } from "./Action";
import { useSurveySections } from "../Contexts/SurveySectionsContext";

export function Alert(props: QuestionProps) {
  const context = useContext(InterfaceContext);
  const { sections } = useSurveySections();
  const { content, variant, dismissible, title, iconName, mode, button, navigateBackToSection, blockProgress } = safeParse(props.Metadata ?? "{}", {}) as {
    content: any, variant: any, dismissible: any, title: any, iconName: any, mode: any,
    button?: {
      content: Record<string, string>,
      action: BulkDownload | VisitLink | string | { kind: string },
      buttonType?: 'regular' | 'action',
      confirmation?: Record<string, string>,
      toast?: Record<string, string>
    },
    navigateBackToSection?: string,
    blockProgress?: boolean
  };

  const [dismissed, setDismissed] = useState(false);

  const { handleClick } = useActionExecutor(
    button?.action as any,
    { confirmation: button?.confirmation, toast: button?.toast },
    props
  );

  const section = useMemo(() =>
    navigateBackToSection
      ? sections.sections?.find(s => {
        const sectionMeta: { sectionId?: string } = typeof s.Metadata === 'string'
          ? safeParse(s.Metadata || "{}")
          : (s.Metadata || {});
        return sectionMeta.sectionId === navigateBackToSection;
      })
      : undefined,
  [navigateBackToSection, sections]);

  const contentMarkdown = useModularMarkdown({
    content: content ?? '',
    info: { ...props.info, uid: props.uid || 'No UID' },
  });

  const titleText = title && typeof title === 'object'
    ? (title[context.lang] || title['en'] || '')
    : (title || '');

  const buttonText = button?.content
    ? (button.content[context.lang] || button.content['en'] || '')
    : '';

  const handleDismiss = () => {
    setDismissed(true);
    props.onDismiss?.();
  };

  if (dismissed) return null;

  return (
    <AlertComponent
      variant={variant}
      title={titleText}
      mode={mode === 'outline' ? 'inline' : 'block'}
      dismissible={dismissible && !blockProgress}
      onDismiss={handleDismiss}
      iconName={iconName}>
      {contentMarkdown}
      {(button || (navigateBackToSection && section)) && (
        <div className="flex justify-end mt-4 gap-2">
          {navigateBackToSection && section && <GoBackToSectionButton sectionId={section.id} />}
          {button && (button.buttonType === 'action'
            ? <AlertComponent.Button onClick={handleClick}>{buttonText}</AlertComponent.Button>
            : <Button onClick={handleClick}>{buttonText}</Button>
          )}
        </div>
      )}
    </AlertComponent>
  );
}
